import React, { useMemo } from "react";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Box, IconButton, Stack } from "@mui/material";
import { PDFTypeIcon } from "assets/material-custom-icons";
import { DataExtractionModal } from "components/Data-extraction/DataExtractionModal";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { currencySign } from "constants/index";
import { useFormatDateHook, usePrompt } from "hooks";
import { MaterialReactTable, MRT_ColumnDef, useMaterialReactTable } from "material-react-table";
import { ApiValidationErrors, IBulkValueRow, IUpdateFileTagData } from "store/types";
import { formatCurrency } from "utils/strings";

import { EffectiveDatePrompt } from "../../../File-repository/Table/EffectiveDatePrompt";
import { InvestorPrompt } from "../../../File-repository/Table/InvestorPrompt";
import { AddValueButton, EditableCell, TagValuePrompt, TextCell, tooltipHeader } from "../components";
import { bulkTableBaseOptions } from "./TableOptions";

interface Props {
  rows: IBulkValueRow[];
  onExtractorClose: () => void;
  errors?: ApiValidationErrors;
  readOnly: boolean;
  onEditTag: (fileId: number, tagId: number, data: IUpdateFileTagData) => void;
}

const toErrorMessages = (value: unknown): string[] => {
  if (typeof value === "string") return [value];
  if (Array.isArray(value)) return value.flatMap(toErrorMessages);
  if (value && typeof value === "object") return Object.values(value).flatMap(toErrorMessages);
  return [];
};

export const BulkReviewLongTable: React.FC<Props> = ({ rows, onExtractorClose, errors, readOnly, onEditTag }) => {
  const formatDate = useFormatDateHook();
  const openPrompt = usePrompt();

  const openFilePreview = async (fileId: number, fileName: string) => {
    await openPrompt({
      componentType: "fullscreen",
      Component: DataExtractionModal,
      props: {
        fileIds: [fileId],
        fileNames: { [fileId]: fileName },
        source: "deal" as const,
        initialShowParameters: true,
        hideDoneButton: true
      }
    });
    onExtractorClose();
  };

  const columns = useMemo<MRT_ColumnDef<IBulkValueRow>[]>(() => {
    const editable = (value: string, isEmpty: boolean, onClick: (e: React.MouseEvent<HTMLDivElement>) => void) => {
      if (readOnly) return <TextCell value={isEmpty ? "—" : value} />;
      if (isEmpty) return <AddValueButton onClick={onClick} />;
      return <EditableCell value={value} onClick={onClick} />;
    };

    const editValue = async (row: IBulkValueRow, e: React.MouseEvent<HTMLDivElement>) => {
      const result = await openPrompt({
        componentType: "popover",
        anchorEl: e.currentTarget as HTMLElement,
        Component: TagValuePrompt,
        props: { currentValue: row.value }
      });
      if (!result) return;
      onEditTag(row.fileId, row.tagId, { value: result.value ?? undefined });
    };

    const editInvestor = async (row: IBulkValueRow, e: React.MouseEvent<HTMLDivElement>) => {
      const result = await openPrompt({
        componentType: "popover",
        anchorEl: e.currentTarget as HTMLElement,
        Component: InvestorPrompt,
        props: {
          currentInvestorId: row.investorId,
          currentAllInvestors: row.allInvestors,
          currentSplitByCommitment: false,
          currentInvestorIds: row.investorIds,
          currentEntireCommitment: row.entireCommitment,
          dealId: row.dealId
        }
      });
      if (!result) return;
      const isGroups = Boolean(result.investorIds.length);
      onEditTag(row.fileId, row.tagId, {
        investor_id: result.investor?.id ?? null,
        all_investors: result.allInvestors,
        split_by_commitment: result.splitByCommitment,
        investor_ids: result.investorIds,
        group_commitment: isGroups ? !result.entireCommitment : false,
        entire_commitment: isGroups ? result.entireCommitment : false
      });
    };

    const editDate = async (
      row: IBulkValueRow,
      field: "effective_date" | "accounting_date",
      current: string | null,
      e: React.MouseEvent<HTMLDivElement>
    ) => {
      const result = await openPrompt({
        componentType: "popover",
        anchorEl: e.currentTarget as HTMLElement,
        Component: EffectiveDatePrompt,
        props: { currentDate: current }
      });
      if (!result) return;
      onEditTag(row.fileId, row.tagId, { [field]: result.effectiveDate });
    };

    return [
      {
        id: "conflictIndicator",
        header: "",
        size: 43,
        minSize: 43,
        grow: false,
        enableSorting: false,
        enableColumnActions: false,
        enableResizing: false,
        Cell: ({ row }) => {
          const backendErrors = toErrorMessages(errors?.[row.original.tagId]);
          const messages = backendErrors.length ? backendErrors : row.original.validationErrors;

          if (messages.length) {
            return (
              <Stack alignItems="center" justifyContent="center" sx={{ width: "100%" }}>
                <MaterialTooltip
                  isShow
                  variant="dark"
                  title={
                    <>
                      {messages.map((message, i) => (
                        <div key={i}>{message}</div>
                      ))}
                    </>
                  }
                >
                  <ErrorOutlineIcon sx={{ fontSize: 18, color: backendErrors.length ? "#d32f2f" : "#e2a23b" }} />
                </MaterialTooltip>
              </Stack>
            );
          }

          return (
            <Stack alignItems="center" justifyContent="center" sx={{ width: "100%" }}>
              <MaterialTooltip title="Complete">
                <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#12a780" }} />
              </MaterialTooltip>
            </Stack>
          );
        }
      },
      {
        id: "measureName",
        accessorKey: "measureName",
        header: "Parameter",
        enableColumnActions: false,
        enableSorting: false,
        Header: tooltipHeader("Parameter"),
        Cell: ({ row }) => <TextCell value={row.original.measureName} />
      },
      {
        id: "value",
        accessorKey: "value",
        header: "Value",
        enableColumnActions: false,
        enableSorting: false,
        Header: tooltipHeader("Value"),
        Cell: ({ row }) =>
          editable(
            row.original.value == null
              ? ""
              : formatCurrency({
                  value: row.original.value,
                  currencySymbol: currencySign[row.original.currency] ?? ""
                }),
            row.original.value == null,
            (e) => editValue(row.original, e)
          )
      },
      {
        id: "round",
        accessorKey: "round",
        header: "Round",
        enableColumnActions: false,
        enableSorting: false,
        Header: tooltipHeader("Round"),
        Cell: ({ row }) =>
          row.original.round == null ? <TextCell value="—" /> : <TextCell value={row.original.round.name} />
      },
      {
        id: "investorName",
        accessorKey: "investorName",
        header: "Investor",
        enableColumnActions: false,
        enableSorting: false,
        Header: tooltipHeader("Investor"),
        Cell: ({ row }) => {
          const { investorId, allInvestors, investorIds } = row.original;
          const isEmpty = !investorId && !allInvestors && !investorIds.length;
          return editable(row.original.investorName, isEmpty, (e) => editInvestor(row.original, e));
        }
      },
      {
        id: "effectiveDate",
        accessorKey: "effectiveDate",
        header: "Effective Date",
        enableColumnActions: false,
        enableSorting: false,
        Header: tooltipHeader("Effective Date"),
        Cell: ({ row }) => {
          const value = row.original.effectiveDate
            ? formatDate({ date: row.original.effectiveDate, format: "DD/MM/YYYY" })
            : "";
          return editable(value, !row.original.effectiveDate, (e) =>
            editDate(row.original, "effective_date", row.original.effectiveDate, e)
          );
        }
      },
      {
        id: "accountingDate",
        accessorKey: "accountingDate",
        header: "Accounting Date",
        enableColumnActions: false,
        enableSorting: false,
        Header: tooltipHeader("Accounting Date"),
        Cell: ({ row }) => {
          const value = row.original.accountingDate
            ? formatDate({ date: row.original.accountingDate, format: "DD/MM/YYYY" })
            : "";
          return editable(value, !row.original.accountingDate, (e) =>
            editDate(row.original, "accounting_date", row.original.accountingDate, e)
          );
        }
      },
      {
        id: "source",
        accessorKey: "source",
        header: "Source",
        enableColumnActions: false,
        enableSorting: false,
        Header: tooltipHeader("Source"),
        Cell: ({ row }) => (
          <MaterialTooltip title={`Open source ${row.original.source}`} variant="dark">
            <IconButton onClick={() => openFilePreview(row.original.fileId, row.original.fileName)}>
              <PDFTypeIcon sx={{ width: 16, height: 16 }} />
            </IconButton>
          </MaterialTooltip>
        )
      }
    ];
  }, [formatDate, errors, readOnly, openPrompt, onEditTag]);

  const table = useMaterialReactTable<IBulkValueRow>({
    ...bulkTableBaseOptions<IBulkValueRow>(),
    defaultColumn: { grow: true, size: 160, minSize: 120 },
    columns,
    data: rows,
    getRowId: (row) => row.rowId
  });

  return (
    <Stack sx={{ borderRadius: "8px", overflow: "hidden", flex: 1, minHeight: 0, minWidth: 0 }}>
      <MaterialReactTable table={table} />
    </Stack>
  );
};
